import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useModulePermissions } from "@/context/ModulePermissionsContext";
import { resolveParaplannerHomeSurfaceId } from "@/components/home/resolveHomeSurfaceId";
import * as AgenticV2API from "@/services/api/agentic-v2";
import type { SurfaceSnapshot } from "@/services/api/agentic-v2/types";
import type { VisibilityScope } from "@/components/agentic-v2/VisibilityFilter";
import { useWindowLive } from "@/components/ui/os-layout/WindowActiveContext";

export const agenticSurfaceKeys = {
  all: ["agentic-surface"] as const,
  list: () => [...agenticSurfaceKeys.all, "list"] as const,
  spec: (surfaceId: string) => [...agenticSurfaceKeys.all, "spec", surfaceId] as const,
  snapshot: (
    surfaceId: string,
    visibility: VisibilityScope,
    platform: string,
    refreshKey: number,
    asUserId?: string,
    assigneeUserId?: string,
  ) =>
    [
      ...agenticSurfaceKeys.all,
      "snapshot",
      surfaceId,
      visibility,
      platform,
      refreshKey,
      asUserId ?? "",
      assigneeUserId ?? "",
    ] as const,
};

function shouldRetrySurfaceSnapshot(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false;
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
      return false;
    }
  }
  return true;
}

export interface UseAgenticSurfaceOptions {
  surfaceId: string;
  visibility?: VisibilityScope;
  asUserId?: string;
  assigneeUserId?: string;
  platform?: "web" | "mobile" | "both";
  refreshKey?: number;
  enabled?: boolean;
  /** When false, fetch even if the OS window is unfocused or minimised. */
  requireWindowLive?: boolean;
}

export function useAgenticSurface({
  surfaceId,
  visibility = "all",
  asUserId,
  assigneeUserId,
  platform = "web",
  refreshKey = 0,
  enabled = true,
  requireWindowLive = true,
}: UseAgenticSurfaceOptions) {
  const queryClient = useQueryClient();
  const isWindowLive = useWindowLive();
  const windowGate = requireWindowLive ? isWindowLive : true;

  const snapshotQuery = useQuery({
    queryKey: agenticSurfaceKeys.snapshot(surfaceId, visibility, platform, refreshKey, asUserId, assigneeUserId),
    queryFn: () =>
      AgenticV2API.getSurfaceSnapshot(surfaceId, {
        visibility,
        platform,
        as_user_id: asUserId,
        assignee_user_id: assigneeUserId,
      }),
    enabled: enabled && windowGate && Boolean(surfaceId),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    retry: shouldRetrySurfaceSnapshot,
    retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 8_000),
  });

  const refreshAll = useCallback(() => {
    // Snapshot fetches take ~1s; live updates can fire faster than that. Let the in-flight
    // fetch land instead of cancelling it, otherwise rapid refreshes never update the UI.
    queryClient.invalidateQueries({ queryKey: agenticSurfaceKeys.all }, { cancelRefetch: false });
  }, [queryClient]);

  return {
    snapshot: snapshotQuery.data as SurfaceSnapshot | undefined,
    isLoading: snapshotQuery.isLoading,
    isFetching: snapshotQuery.isFetching,
    isError: snapshotQuery.isError,
    error: snapshotQuery.error,
    refreshAll,
  };
}

export function useAgenticSurfaces() {
  return useQuery({
    queryKey: agenticSurfaceKeys.list(),
    queryFn: () => AgenticV2API.listSurfaces(),
    staleTime: 60_000,
    retry: shouldRetrySurfaceSnapshot,
    retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 8_000),
  });
}

/** Widget layout without task buckets, meetings, or stats (fast path for progressive Home). */
export function useSurfaceSpec(surfaceId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: agenticSurfaceKeys.spec(surfaceId ?? ""),
    queryFn: () => AgenticV2API.getSurfaceSpec(surfaceId!),
    enabled: enabled && Boolean(surfaceId),
    staleTime: 5 * 60 * 1000,
  });
}

export function usePrimaryHomeSurface() {
  const { user } = useAuth();
  const { permissions } = useModulePermissions();
  const surfacesQuery = useAgenticSurfaces();
  const fallbackSurfaceId = resolveParaplannerHomeSurfaceId(user?.company_id, {
    role: user?.role,
    modules: permissions?.modules,
  });
  const surfacesReady = surfacesQuery.isSuccess || surfacesQuery.isError;

  const resolved = useMemo(() => {
    const surfaces = surfacesQuery.data ?? [];
    const primary = surfaces.find((surface) => surface.is_primary);
    const surfaceId = primary?.id ?? (surfacesReady ? fallbackSurfaceId : null);
    const meta = primary ?? surfaces.find((surface) => surface.id === surfaceId);

    return {
      surfaceId,
      surfaceContext: meta?.context ?? null,
      taskFilter: meta?.task_filter ?? null,
      caseloadVisibility: meta?.caseload_visibility ?? null,
      isLoading: surfacesQuery.isLoading || !surfacesReady,
    };
  }, [
    surfacesQuery.data,
    surfacesQuery.isLoading,
    surfacesReady,
    fallbackSurfaceId,
  ]);

  return resolved;
}
